import { gsap } from "gsap";
import { AnimatedSprite, Container, Graphics, Sprite, Texture } from "pixi.js";

import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { getRandomBetween } from "../../../abstracts/utils/JSUtils";
import { playSound } from "../../../abstracts/utils/SoundUtils";
import { davidPanningReelStop } from "../../UCConfig";
import { UCSoundNames, UCSpineNames } from "../../assetsLoader/UCAssetsNames";
import { BlockerStates, UCSymbolNames } from "../../types/UCEnums";
import { fishLandSounds } from "../Sound/UCSoundController";
import UCReel, { reelLandSounds } from "./UCReel";
import { UCSymbol, isFish } from "./symbols/UCSymbol";

const unlockSounds: UCSoundNames[] = [
    UCSoundNames.BLOCKER_OPEN_1,
    UCSoundNames.BLOCKER_OPEN_2,
    UCSoundNames.BLOCKER_OPEN_3,
];

let currentTeaseSoundIndex: number = 0;

export const playBlockerOpenSound = () => {
    currentTeaseSoundIndex = getRandomBetween(0, unlockSounds.length - 1, true);
    playSound({
        name: unlockSounds[currentTeaseSoundIndex],
    });
};

export class UCReelBlocker extends Container {
    protected anticipationSpine: AbstractSpine;
    protected mainSpine: AbstractSpine;
    protected highlightAnimatedSprite: AnimatedSprite;
    protected highlightTimeline: gsap.core.Timeline;
    protected hasXBetActive: boolean = false;
    protected separator: Sprite;
    protected reel: UCReel;

    public isTop: boolean = true;
    public symbol: UCSymbol;
    public isOpenDataFromServer: boolean = false;
    public isAlreadyRevealed: boolean = false;
    public state: BlockerStates = BlockerStates.CLOSED;
    public fakeBackground: Sprite;
    public symbolClone: UCSymbol;
    public symbolCloneMask: Graphics;
    public hasClone: boolean = false;

    constructor(isTop: boolean, reel: UCReel) {
        super();

        this.isTop = isTop;
        this.reel = reel;

        this.init();
    }

    protected init() {
        this.anticipationSpine = new AbstractSpine(UCSpineNames.REEL_BLOCKER_ANTICIPATION);
        this.anticipationSpine.name = `ReelBlockerAnticipation`;
        this.anticipationSpine.width -= 4;
        this.anticipationSpine.position.set(69, 112);

        this.addChild(this.anticipationSpine);

        this.mainSpine = new AbstractSpine(UCSpineNames.REEL_BLOCKER);
        this.mainSpine.name = `ReelBlocker`;
        this.mainSpine.position.set(69, 113);
        this.addChild(this.mainSpine);

        const textureArray = [];

        for (let i = 0; i < 20; i++) {
            const currentTextureIndex: string = i < 10 ? `00${i}` : `0${i}`;
            const texture = Texture.from(`LR_anticipation/LR_anticipation_${currentTextureIndex}`);
            textureArray.push(texture);
        }
        this.highlightAnimatedSprite = new AnimatedSprite(textureArray);
        this.highlightAnimatedSprite.scale.set(0.5);
        this.highlightAnimatedSprite.anchor.set(0.5);

        this.highlightAnimatedSprite.position.set(70, 60);

        this.separator = new Sprite(Texture.from(`separator`));
        this.separator.name = `separator`;
        this.separator.anchor.set(0, 0.5);
        this.separator.x = -1.7;
        this.separator.y = this.isTop ? 107 : 3;
        this.addChild(this.separator);
    }

    public showSeparator() {
        if (this.separator.alpha !== 1) {
            this.separator.alpha = 0;
            this.addChildAt(this.separator, 0);

            gsap.to(this.separator, {
                alpha: 1,
                duration: 0.15,
            });
        }
    }

    public hideSeparator() {
        gsap.to(this.separator, {
            alpha: 0,
            duration: 0.15,
        });
    }

    public onXbetEnabled() {
        this.hasXBetActive = true;

        if (this.state !== BlockerStates.OPEN && this.isTop) {
            playBlockerOpenSound();
        }

        this.open();
    }

    public onXbetDisabled() {
        this.hasXBetActive = false;

        if (this.state === BlockerStates.OPEN && !this.isOpenDataFromServer && this.isTop) {
            playSound({ name: UCSoundNames.BLOCKER_CLOSE });
        }

        this.close(true);
    }

    public startTeaseAnimation(): gsap.core.Timeline {
        const tl = gsap.timeline({});
        tl.call(() => this.showSeparator(), undefined, 0);

        if (this.state !== BlockerStates.OPEN) {
            return tl;
        }

        const showAnticipationTween = gsap.fromTo(
            this.anticipationSpine,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.3,
                onStart: () => {
                    this.addChild(this.anticipationSpine);
                    this.anticipationSpine.getSpineTween(`Anticipation`, true);
                    this.addChild(this.highlightAnimatedSprite);

                    this.addChild(this.mainSpine);
                },
            }
        );

        tl.add(showAnticipationTween, 0);

        const highlightTween = gsap.fromTo(
            this.highlightAnimatedSprite,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.2,
                onStart: () => {
                    this.highlightAnimatedSprite.loop = true;
                    this.highlightAnimatedSprite.gotoAndPlay(0);
                },
            }
        );

        tl.add(highlightTween);

        return tl;
    }

    public stopTeaseAnimation(duration: number = 0.3): gsap.core.Timeline {
        const tl = gsap.timeline({});
        const hideHighlightSpriteAnimation = gsap.to(this.highlightAnimatedSprite, {
            alpha: 0,
            duration: duration,
            onComplete: () => {
                this.highlightTimeline?.pause();
                this.highlightTimeline?.kill();
                this.highlightTimeline?.clear();

                this.highlightAnimatedSprite.stop();
                this.highlightAnimatedSprite.alpha = 0;
                this.highlightAnimatedSprite.animationSpeed = 1;
                this.removeChild(this.highlightAnimatedSprite);
            },
        });

        tl.add(hideHighlightSpriteAnimation, 0);

        const hideAnticipationAnimation = gsap.to(this.anticipationSpine, {
            alpha: 0,
            duration: duration * 0.8,
            onComplete: () => {
                this.anticipationSpine.reset();
            },
        });

        tl.add(hideAnticipationAnimation, 0);

        return tl;
    }

    public open(isRestore: boolean = false): gsap.core.Timeline {
        if (this.state === BlockerStates.OPEN) {
            return gsap.timeline();
        }

        this.state = BlockerStates.OPEN;

        const tl = gsap.timeline({
            onStart: () => {
                if (!this.isAlreadyRevealed && !isRestore) {
                    this.startTeaseAnimation();
                    this.isAlreadyRevealed = true;
                }
            },
        });

        const unlockAnimation = this.mainSpine.getSpineTween(
            this.isTop ? `unlock_top` : `unlock_bottom`,
            false
        );
        tl.add(unlockAnimation);

        if (isRestore) {
            const restoreLabel: string = `restoreLabel`;
            tl.addLabel(restoreLabel);

            tl.totalDuration(0.1);
        }

        return tl;
    }

    public close(fromXbet: boolean = false): gsap.core.Tween | gsap.core.Timeline {
        if (this.state === BlockerStates.CLOSED || (fromXbet && this.isOpenDataFromServer)) {
            return gsap.timeline();
        }

        if (this.hasXBetActive) {
            const tl = gsap.timeline({});

            tl.add(this.startTeaseAnimation());

            return tl;
        }

        this.state = BlockerStates.CLOSED;

        const tl = gsap.timeline({
            onStart: () => {
                this.stopTeaseAnimation();

                if (!this.isOpenDataFromServer) {
                    this.isAlreadyRevealed = false;
                }
            },
        });

        const lockBlockerAnimation = this.mainSpine.getSpineTween(
            this.isTop ? `lock_top` : `lock_bottom`
        );
        lockBlockerAnimation.timeScale(2.2);

        tl.add(lockBlockerAnimation, 0);

        tl.add(this.hideSeparator, 0);

        return tl;
    }

    public revealNormalSymbol(): gsap.core.Timeline {
        const tl = gsap.timeline({
            onStart: () => {
                this.highlightAnimatedSprite.animationSpeed += 1;

                if (this.symbol.name === UCSymbolNames.WILD) {
                    // if (!symbol.isGold && symbolIndex > 1 && symbolIndex < 5) {
                    playSound({
                        name: UCSoundNames.GOLD_WILD_LAND,
                        stereo: davidPanningReelStop[this.reel.reelId],
                    });
                } else if (this.symbol.name === UCSymbolNames.MYSTERY) {
                    playSound({
                        name: UCSoundNames.XWAYS_LAND,
                        stereo: davidPanningReelStop[this.reel.reelId],
                        playOnceMinDuration: 2,
                    });
                } else if (this.symbol.name === UCSymbolNames.BIG_FISH) {
                    playSound({
                        name: UCSoundNames.BIG_FISH_LAND,
                        stereo: davidPanningReelStop[this.reel.reelId],
                        playOnceMinDuration: 2,
                    });
                } else if (isFish(this.symbol.name as UCSymbolNames)) {
                    const fishNumber = +this.symbol.name.replace(/^\D+/g, ``);

                    // if (symbolIndex > 1 && symbolIndex < 5) {
                    playSound({
                        name: fishLandSounds[fishNumber - 1],
                        stereo: davidPanningReelStop[this.reel.reelId],
                        playOnceMinDuration: 2,
                    });
                }
            },
        });

        const stopTeaseAnimation = this.stopTeaseAnimation();

        tl.add(stopTeaseAnimation);

        const scaleUpSymbol = gsap.to(this.symbol.getMainSpine(), {
            pixi: { width: `+=10`, height: `+=10`, y: `+=5` },
            yoyo: true,
            repeat: 1,
            duration: 0.12,
        });

        tl.add(scaleUpSymbol, 0.1);

        return tl;
    }

    public revealFsSymbol(symbolName: UCSymbolNames, isSfs: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline({
            onStart: () => {
                this.addChildAt(this.symbolClone, 0);
                this.addChildAt(this.fakeBackground, 0);

                playSound({ name: reelLandSounds[this.reel.reelId] });

                if (symbolName === UCSymbolNames.WILD) {
                    playSound({
                        name: UCSoundNames.GOLD_WILD_LAND,
                        stereo: davidPanningReelStop[this.reel.reelId],
                    });
                } else if (symbolName === UCSymbolNames.MYSTERY) {
                    playSound({
                        name: UCSoundNames.XWAYS_LAND,
                        stereo: davidPanningReelStop[this.reel.reelId],
                        playOnceMinDuration: 2,
                    });
                } else if (symbolName === UCSymbolNames.BIG_FISH) {
                    playSound({
                        name: UCSoundNames.BIG_FISH_LAND,
                        stereo: davidPanningReelStop[this.reel.reelId],
                        playOnceMinDuration: 2,
                    });
                } else if (isFish(symbolName as UCSymbolNames)) {
                    const fishNumber = +symbolName.replace(/^\D+/g, ``);

                    playSound({
                        name: fishLandSounds[fishNumber - 1],
                        stereo: davidPanningReelStop[this.reel.reelId],
                        playOnceMinDuration: 2,
                    });
                }
            },
        });

        tl.add(this.reel.revealFsSymbol(symbolName, this.isTop, isSfs), 0);

        tl.add(this.stopTeaseAnimation(), 0.2);

        return tl;
    }

    public hideFSSymbol(): void {
        if (!this.hasClone) {
            return;
        }

        const tl = gsap.timeline({
            onStart: () => {
                this.symbol.visible = true;
            },
            onComplete: () => {
                if (this.symbolClone !== null) {
                    this.symbolClone.destroy();
                }

                if (this.fakeBackground !== null) {
                    this.fakeBackground.destroy();
                }

                if (this.symbolCloneMask !== null) {
                    this.symbolCloneMask.destroy();
                }

                this.hasClone = false;
            },
        });

        const hideSymbolTween = gsap.fromTo(
            this.symbolClone,
            { alpha: 1 },
            { alpha: 0, duration: 0.02 }
        );
        tl.add(hideSymbolTween, 0);

        const hideBgTween = gsap.fromTo(
            this.fakeBackground,
            { alpha: 1 },
            { alpha: 0, duration: 0.02 }
        );
        tl.add(hideBgTween, 0);
    }
}
